import {yupResolver} from '@hookform/resolvers/yup';
import React, {useEffect, useState} from 'react';
import {useForm} from 'react-hook-form';
import {useDispatch} from 'react-redux';
import {v4 as uuidv4} from 'uuid';
import * as yup from 'yup';
import {useInstanceDetailPage} from '../../../../api/hooks/useInstanceDetail.hook';
import {removeSellInstanceFixPriceStatus, sellInstanceFixPrice} from '../../../../redux/slices/resale-slice';
import {ButtonVariantsEnum} from '../../../../utils/_enums/button-variants.enum';
import {TypographyVariantsEnum} from '../../../../utils/_enums/typeography-variants.enum';
import {HashGenerator} from '../../../../utils/_helper/hash-generator';
import {getLocalePrice} from '../../../../utils/_helper/locale-helper';
import {signHash} from '../../../../utils/_helper/sign-helper';
import {dateToISOString} from '../../../../utils/_helper/time-helper';
import {useAppSelector} from '../../../../utils/_hooks/hooks';
import {Button} from '../../button/button';
import {Checkbox} from '../../checkbox/checkbox';
import {Input} from '../../input-fields/input/input';
import {Typography} from '../../typography/typography';
import SalesBoxConnectWallet from '../connect-wallet/sales-box-connect-wallet';
import styles from './sell-nft-box.module.scss';

const SellNftBox = () => {
    const data = useInstanceDetailPage();
    const {
        instance,
        walletId,
        ethereumPriceData,
        sellInstanceFixPriceModel
    } = useAppSelector((state) => ({
        instance: state.productSlice.instance,
        walletId: state.blockchainSlice.walletId,
        ethereumPriceData: state.blockchainSlice.ethereumPriceData,
        sellInstanceFixPriceModel: state.resaleSlice.sellInstanceFixPriceModel
    }));
    const [onConfirm, setOnConfirm] = useState<boolean>(false);
    const [exchangeCount, setExchangeCount] = useState<number>(0);
    const [isMetaMaskOpen, setIsMetaMaskOpen] = useState(false);

    const schema = yup.object({
        startDate: yup
            .string()
            .required(data?.startDate.fieldErrorMessages?.required),
        price: yup
            .string()
            .required(data?.ethPrice.fieldErrorMessages?.required)
    });
    const dispatch = useDispatch();

    const {
        register,
        getValues,
        handleSubmit,
        watch,
        formState: {errors}
    } = useForm({
        mode: 'onSubmit',
        resolver: yupResolver(schema)
    });

    const watchCheckboxFields = watch(['conditionsConfirmed', 'agbConfirmed']);
    const watchPrice = watch('price');

    useEffect(() => {
        if (ethereumPriceData) {
            setExchangeCount(
                (watchPrice?.replace(/,/g, '.') as number) /
                ethereumPriceData.exchangeRate
            );
        }
    }, [watchPrice]);

    useEffect(() => {
        if (sellInstanceFixPriceModel.status && sellInstanceFixPriceModel.status !== 200) {
            dispatch(removeSellInstanceFixPriceStatus());
        }
    }, [sellInstanceFixPriceModel.status]);

    const submitForm = () => {
        setOnConfirm(true);
    };

    const sellNft = async () => {
        if (instance) {
            const tradeId = uuidv4();
            const ethPrice = Number(getValues('price').replace(/,/g, '.'));
            const hash = HashGenerator.createResaleFixPriceHash(
                instance.variant?.salesAttribute?.contract?.contractAddress ?? '',
                instance.id,
                tradeId,
                ethPrice
            );
            if (hash) {
                setIsMetaMaskOpen(true);
                try {
                    const signRes = await signHash(hash);
                    dispatch(
                        sellInstanceFixPrice({
                            instanceId: instance.id,
                            data: {
                                price: ethPrice,
                                signature: signRes,
                                tradeID: tradeId,
                                startDate: dateToISOString(getValues('startDate'))
                            }
                        })
                    );
                    setIsMetaMaskOpen(false);
                } catch {
                    setIsMetaMaskOpen(false);
                    return;
                }
            }
        }
    };

    if (data && instance) {
        return (
            <div>
                {walletId ? (
                    <div>
                        {walletId === instance.ownerWallet ? (
                            <div>
                                {onConfirm ? (
                                    <div className={`${styles['sell-nft-box']} ${styles['confirmation-box']}`}>
                                        <Checkbox
                                            name={'conditionsConfirmed'}
                                            register={register}
                                        >{`${data.confirmListingPart1} ${
                                            instance.variant?.salesAttribute?.contract?.contractAddress ?? ''
                                        } ${data.confirmListingPart2} ${
                                            instance?.id
                                        }
                                 ${data.confirmListingPart3} ${getValues(
                                            'price'
                                        )}Ξ ${
                                            data.confirmListingPart4
                                        }`}</Checkbox>
                                        <Checkbox
                                            name={'agbConfirmed'}
                                            register={register}
                                        >
                                            {data.confirmAgb.fieldTooltip ?? ''}
                                        </Checkbox>
                                        <div className={styles['sell-actions']}>
                                            <Button
                                                disabled={
                                                    isMetaMaskOpen ||
                                                    sellInstanceFixPriceModel.pending
                                                }
                                                onClick={() =>
                                                    setOnConfirm(false)
                                                }
                                                className={
                                                    styles['abort-sale-button']
                                                }
                                                variant={
                                                    ButtonVariantsEnum.SECONDARY_OUTLINED
                                                }
                                            >
                                                {data.backButton.buttonText}
                                            </Button>
                                            <Button
                                                disabled={!watchCheckboxFields[0] || !watchCheckboxFields[1]}
                                                pending={sellInstanceFixPriceModel.pending || isMetaMaskOpen}
                                                className={styles['confirm-sale-button']}
                                                onClick={sellNft}
                                            >
                                                {data.sellButton.buttonText}
                                            </Button>
                                        </div>
                                    </div>
                                ) : (
                                    <form
                                        onSubmit={handleSubmit(submitForm)}
                                        className={styles['sell-nft-box']}
                                    >
                                        {
                                            instance
                                                ? <Typography>
                                                    {instance.creatoken
                                                        ? data?.cannotSellCreatoken?.content
                                                        : data?.sellNftDescription?.content}
                                                </Typography>
                                                : undefined
                                        }
                                        <div>
                                            <div
                                                className={styles['sell-nft-box-left-wrapper']}
                                            >
                                                <Input
                                                    register={register}
                                                    placeholder={data.startDate.fieldLabel}
                                                    disabled={instance?.creatoken}
                                                    title={data.startDate.fieldLabel}
                                                    errorMessage={errors?.['startDate']?.message}
                                                    name={'startDate'}
                                                    type={'datetime-local'}
                                                />
                                                <Input
                                                    register={register}
                                                    placeholder={
                                                        data.ethPrice.fieldLabel
                                                    }
                                                    disabled={instance?.creatoken}
                                                    errorMessage={
                                                        errors?.['price']
                                                            ?.message
                                                    }
                                                    title={
                                                        data.ethPrice.fieldLabel
                                                    }
                                                    className={
                                                        styles['lower-input']
                                                    }
                                                    name={'price'}
                                                />
                                                <div
                                                    className={
                                                        styles['exchange-rate']
                                                    }
                                                >
                                                    <Typography
                                                        variant={TypographyVariantsEnum.CAPTION}
                                                    >
                                                        {exchangeCount > 0
                                                            ? `(~${getLocalePrice(exchangeCount)}€)`
                                                            : ''}
                                                    </Typography>
                                                </div>
                                            </div>
                                            <div
                                                className={
                                                    styles[
                                                        'sell-nft-box-right-wrapper'
                                                        ]
                                                }
                                            >
                                                <Button
                                                    disabled={instance?.creatoken}
                                                    className={
                                                        styles[
                                                            'confirm-sale-button'
                                                            ]
                                                    }
                                                >
                                                    {data.sellButton.buttonText}
                                                </Button>
                                            </div>
                                        </div>
                                    </form>
                                )}
                            </div>
                        ) : (
                            <div className={styles['connect-wallet-wrapper']}>
                                <Typography
                                    variant={TypographyVariantsEnum.BODY_SMALL}
                                >
                                    {
                                        data.correctAccountWrongWalletPrompt
                                            .content
                                    }
                                </Typography>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className={styles['connect-wallet-wrapper']}>
                        <Typography variant={TypographyVariantsEnum.BODY_SMALL}>
                            {data.sellNftDescription.content}
                        </Typography>
                        <SalesBoxConnectWallet/>
                    </div>
                )}
            </div>
        );
    } else {
        return <div/>;
    }
};

export default SellNftBox;
